import { api } from './client';
import type { User, Session } from '@/types';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/types';

// Response types (what the frontend expects)
export interface AuthResponse {
  user: User;
  session: Session;
}

export interface RegisterBeginResponse {
  options: PublicKeyCredentialCreationOptionsJSON;
  challengeId: string;
}

export interface LoginBeginResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
  challengeId: string;
}

// Backend response types (snake_case, actual API format)
interface RegisterBeginApiResponse {
  challenge_id: string;
  options: {
    publicKey: PublicKeyCredentialCreationOptionsJSON;
  };
}

interface LoginBeginApiResponse {
  challenge_id: string;
  options: {
    publicKey: PublicKeyCredentialRequestOptionsJSON;
  };
}

interface AuthFinishApiResponse {
  user_id: string;
  email: string;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
}

// Helper to decode JWT payload (without verification - just for reading claims)
function decodeJwtPayload(token: string): { plan?: string; email?: string; sub?: string } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

// Helper to convert backend auth response to frontend format
function convertAuthResponse(response: AuthFinishApiResponse): AuthResponse {
  const now = new Date().toISOString();
  
  // Store the access token for authenticated requests
  api.setAccessToken(response.tokens.access_token);
  
  // Decode JWT to get plan from claims
  const claims = decodeJwtPayload(response.tokens.access_token);
  const plan = (claims?.plan || 'free') as 'free' | 'pro' | 'team' | 'enterprise';
  const planLimits = getPlanLimits(plan);
  
  return {
    user: {
      id: response.user_id,
      email: response.email,
      plan,
      createdAt: now,
      lastLoginAt: now,
      planLimits,
    },
    session: {
      id: response.user_id, // Use user_id as session id placeholder
      userId: response.user_id,
      createdAt: now,
      expiresAt: new Date(Date.now() + response.tokens.expires_in * 1000).toISOString(),
      deviceInfo: {
        browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                 navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                 navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
        os: navigator.platform || 'Unknown',
        device: 'Desktop',
      },
      ipAddress: '',
      isCurrent: true,
    },
  };
}

// API functions
export async function registerBegin(email: string): Promise<RegisterBeginResponse> {
  const response = await api.post<RegisterBeginApiResponse>('/auth/register/begin', { email });
  return {
    challengeId: response.challenge_id,
    options: response.options.publicKey,
  };
}

export async function registerFinish(
  challengeId: string,
  credential: RegistrationResponseJSON
): Promise<AuthResponse> {
  const response = await api.post<AuthFinishApiResponse>('/auth/register/finish', {
    challenge_id: challengeId,
    credential,
  });
  return convertAuthResponse(response);
}

export async function loginBegin(email?: string): Promise<LoginBeginResponse> {
  const response = await api.post<LoginBeginApiResponse>('/auth/login/begin', { email }, { skipAuth: true });
  return {
    challengeId: response.challenge_id,
    options: response.options.publicKey,
  };
}

export async function loginFinish(
  challengeId: string,
  credential: AuthenticationResponseJSON
): Promise<AuthResponse> {
  const response = await api.post<AuthFinishApiResponse>('/auth/login/finish', {
    challenge_id: challengeId,
    credential,
  }, { skipAuth: true });
  return convertAuthResponse(response);
}

export async function logout(): Promise<void> {
  api.setAccessToken(null); // Clear the token
  return api.post('/auth/logout');
}

export async function getCurrentUser(): Promise<AuthResponse> {
  interface MeApiResponse {
    user: {
      id: string;
      email: string;
      plan: string;
      created_at: string;
    };
    session: {
      id: string;
      expires_at: string;
    };
  }
  
  const response = await api.get<MeApiResponse>('/auth/me');
  
  // Convert plan string to plan limits
  const planLimits = getPlanLimits(response.user.plan);
  
  return {
    user: {
      id: response.user.id,
      email: response.user.email,
      plan: response.user.plan as 'free' | 'pro' | 'team' | 'enterprise',
      createdAt: response.user.created_at,
      lastLoginAt: null,
      planLimits,
    },
    session: {
      id: response.session.id,
      userId: response.user.id,
      createdAt: new Date().toISOString(),
      expiresAt: response.session.expires_at,
      deviceInfo: {
        browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                 navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                 navigator.userAgent.includes('Safari') ? 'Safari' : 'Unknown',
        os: navigator.platform || 'Unknown',
        device: 'Desktop',
      },
      ipAddress: '',
      isCurrent: true,
    },
  };
}

// Helper to get plan limits based on plan name
function getPlanLimits(plan: string): { maxAgents: number; maxSessionDuration: number; maxConcurrentSessions: number; features: string[] } {
  switch (plan) {
    case 'enterprise':
      return { maxAgents: 100, maxSessionDuration: 0, maxConcurrentSessions: 50, features: ['custom_subdomain', 'priority_support', 'sla', 'audit_logs'] };
    case 'team':
      return { maxAgents: 20, maxSessionDuration: 1440, maxConcurrentSessions: 20, features: ['custom_subdomain', 'team_management'] };
    case 'pro':
      return { maxAgents: 5, maxSessionDuration: 1440, maxConcurrentSessions: 5, features: ['custom_subdomain'] };
    default:
      return { maxAgents: 1, maxSessionDuration: 60, maxConcurrentSessions: 1, features: [] };
  }
}

export async function refreshSession(): Promise<{ session: Session }> {
  return api.post<{ session: Session }>('/auth/refresh');
}

// TOTP Login
export interface TotpLoginResponse {
  user: User;
  session: Session;
}

export async function loginWithTotp(email: string, code: string): Promise<TotpLoginResponse> {
  const response = await api.post<AuthFinishApiResponse>('/auth/totp/login', { email, code }, { skipAuth: true });
  return convertAuthResponse(response);
}

// Email Verification
export interface SendVerificationCodeResponse {
  message: string;
}

export interface VerifyCodeResponse {
  verified: boolean;
  message: string;
}

export interface CheckVerificationStatusResponse {
  verified: boolean;
}

export async function sendVerificationCode(email: string): Promise<SendVerificationCodeResponse> {
  return api.post<SendVerificationCodeResponse>('/auth/email/send-code', { email }, { skipAuth: true });
}

export async function verifyEmailCode(email: string, code: string): Promise<VerifyCodeResponse> {
  return api.post<VerifyCodeResponse>('/auth/email/verify', { email, code }, { skipAuth: true });
}

export async function checkEmailVerificationStatus(email: string): Promise<CheckVerificationStatusResponse> {
  return api.post<CheckVerificationStatusResponse>('/auth/email/status', { email }, { skipAuth: true });
}
